/**
 * Group Thread Tags — live controller for the global chat surfaces.
 *
 * Owns everything the header chips and the thread-list filter need:
 *   • the shared tag documents for every thread the signed-in user participates
 *     in (server-mediated; the server authorizes per thread),
 *   • the active thread's editable tag state,
 *   • the account-scoped filter and the filtered thread list.
 *
 * TWO-KEY ENABLEMENT. Tag surfaces render only when BOTH agree:
 *   1. `enabled` (client): the account-scoped build flag
 *      (`REACT_APP_GROUP_THREAD_TAGS_ACCOUNTS`) — see `featureFlags.ts`.
 *   2. `enabled` (server): the `ops/groupThreadTags` allowlist, reported back on
 *      the list response.
 * Either key alone can switch the feature off, and the server key takes effect
 * within its cache TTL with no frontend redeploy — that is the rollback path.
 *
 * AVAILABILITY vs EMPTINESS. `listThreadTags` returns `null` when the listing
 * could not be completed. That is held distinct from "no tags exist": on a
 * transient failure we keep the last known documents rather than collapsing to
 * an empty map, because an empty map plus an active filter would hide every
 * thread in the sidebar and read as data loss.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_WORKFLOW_LABEL_ALLOWLIST,
  PRIORITY_LADDER,
  TAG_STATUSES,
  threadMatchesTagFilter,
  type TagPatchIntent,
  type ThreadTagFilter,
  type ThreadTagsDoc,
} from '../../services/threadTags';
import type { ChatThread } from './types';
import { listThreadTags, patchThreadTags, readThreadTags } from '../../repositories/threadTagsClient';

type ThreadTagsView = Pick<ThreadTagsDoc, 'status' | 'priority' | 'ownerUserId' | 'workflowLabels' | 'version'>;

interface ThreadMeta {
  canWrite: boolean;
  participantIds: string[];
}

function defaultTags(): ThreadTagsView {
  return {
    status: 'open',
    priority: 'normal',
    ownerUserId: null,
    workflowLabels: [],
    version: 0,
  };
}

function indexByThread(docs: ThreadTagsDoc[]): Record<string, ThreadTagsDoc> {
  const out: Record<string, ThreadTagsDoc> = {};
  for (const doc of docs) {
    if (doc && typeof doc.threadId === 'string' && doc.threadId) out[doc.threadId] = doc;
  }
  return out;
}

function nextInRing<T>(all: readonly T[], current: T): T {
  const index = all.indexOf(current);
  if (index < 0) return all[0];
  return all[(index + 1) % all.length];
}

function isFilterEmpty(filter: ThreadTagFilter): boolean {
  return !filter.status?.length && !filter.labels?.length && !filter.minPriority && filter.ownerUserId === undefined;
}

export interface UseThreadTagsControllerArgs {
  /** Client-side account-scoped flag (key 1 of 2). */
  enabled: boolean;
  accountId: string | null | undefined;
  actorId: string | null | undefined;
  activeThreadId: string | null;
  threads: ChatThread[];
}

export interface UseThreadTagsControllerResult {
  /** Both keys agreed AND a listing has succeeded. Gates every tag surface. */
  enabled: boolean;
  /** True when the last listing attempt failed; UI may show a soft notice. */
  unavailable: boolean;
  filter: ThreadTagFilter;
  setFilter: (next: ThreadTagFilter) => void;
  activeTags: ThreadTagsView | null;
  /** False when the caller is a viewer in the active thread (read-only). */
  canEditActive: boolean;
  visibleThreads: ChatThread[];
  tagsForThread: (threadId: string) => ThreadTagsView | null;
  onCycleStatus: () => void;
  onCyclePriority: () => void;
  onCycleOwner: () => void;
  onRemoveLabel: (label: string) => void;
  onAddLabel: () => void;
}

export function useThreadTagsController(
  args: UseThreadTagsControllerArgs,
): UseThreadTagsControllerResult {
  const { enabled: clientEnabled, accountId, actorId, activeThreadId, threads } = args;
  const [filter, setFilter] = useState<ThreadTagFilter>({});
  const [docsByThread, setDocsByThread] = useState<Record<string, ThreadTagsDoc>>({});
  const [metaByThread, setMetaByThread] = useState<Record<string, ThreadMeta>>({});
  const [serverEnabled, setServerEnabled] = useState<boolean | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  // Reload when the thread set changes identity, not on every array rebuild.
  const threadKey = threads.map((t) => t.id).join('|');

  useEffect(() => {
    if (!clientEnabled || !accountId || !actorId) {
      setDocsByThread({});
      setMetaByThread({});
      setServerEnabled(null);
      setUnavailable(false);
      return;
    }
    let alive = true;
    // No `workspaceId` narrowing: the server authorizes on thread
    // participation, so a user invited into another account's thread still
    // receives that thread's shared tags.
    void listThreadTags({}).then((result) => {
      if (!alive) return;
      if (!result) {
        // Keep the last known documents; only mark the surface as degraded.
        setUnavailable(true);
        return;
      }
      setUnavailable(false);
      setServerEnabled(result.enabled);
      if (!result.enabled) {
        // Server-side rollback: drop all state so nothing lingers on screen.
        setDocsByThread({});
        setMetaByThread({});
        return;
      }
      setDocsByThread(indexByThread(result.docs));
      const meta: Record<string, ThreadMeta> = {};
      for (const row of result.threads) {
        if (!row.threadId) continue;
        meta[row.threadId] = { canWrite: row.canWrite, participantIds: row.participantIds };
      }
      setMetaByThread(meta);
    });
    return () => {
      alive = false;
    };
  }, [clientEnabled, accountId, actorId, threadKey]);

  // Both keys must agree. `serverEnabled === null` (not yet loaded) renders
  // nothing, so tag controls never flash in and then disappear.
  const enabled = clientEnabled && serverEnabled === true;

  const docsRef = useRef(docsByThread);
  docsRef.current = docsByThread;

  // Refresh the thread the user just opened, so another participant's edit shows
  // up on thread switch rather than only after a full reload.
  //
  // Guarded by version: a slow response for a thread that has since been edited
  // locally must not overwrite the newer state. Versions are server-assigned and
  // monotonic per document, so `fetched.version < held.version` means the
  // response is stale and is dropped.
  useEffect(() => {
    if (!enabled || !activeThreadId) return;
    let alive = true;
    void readThreadTags(activeThreadId).then((fetched) => {
      if (!alive || !fetched) return;
      setDocsByThread((prev) => {
        const held = prev[activeThreadId];
        if (held && fetched.version < held.version) return prev;
        return { ...prev, [activeThreadId]: fetched };
      });
    });
    return () => {
      alive = false;
    };
  }, [enabled, activeThreadId]);

  const viewFor = useCallback((threadId: string): ThreadTagsView => {
    const doc = docsByThread[threadId];
    if (!doc) return defaultTags();
    return {
      status: doc.status,
      priority: doc.priority,
      ownerUserId: doc.ownerUserId,
      workflowLabels: doc.workflowLabels,
      version: doc.version,
    };
  }, [docsByThread]);

  const activeTags = useMemo(() => {
    if (!enabled || !activeThreadId) return null;
    return viewFor(activeThreadId);
  }, [enabled, activeThreadId, viewFor]);

  const canEditActive = useMemo(() => {
    if (!enabled || !activeThreadId) return false;
    const meta = metaByThread[activeThreadId];
    // Unknown thread (not in the shared listing) => no edit affordance.
    return meta ? meta.canWrite : false;
  }, [enabled, activeThreadId, metaByThread]);

  const tagsForThread = useCallback((threadId: string): ThreadTagsView | null => {
    if (!enabled || !threadId) return null;
    return viewFor(threadId);
  }, [enabled, viewFor]);

  const visibleThreads = useMemo(() => {
    if (!enabled || isFilterEmpty(filter)) return threads;
    return threads.filter((thread) => threadMatchesTagFilter(viewFor(thread.id), filter));
  }, [enabled, threads, filter, viewFor]);

  const applyPatch = useCallback(async (intent: TagPatchIntent) => {
    const threadId = (activeThreadId ?? '').trim();
    if (!enabled || !threadId) return;
    if (!(metaByThread[threadId]?.canWrite ?? false)) return;
    const result = await patchThreadTags({
      threadId,
      // Asserting the thread's own workspace (not the caller's account) — the
      // server refuses a mismatch rather than re-homing the document.
      workspaceId: docsRef.current[threadId]?.workspaceId ?? '',
      intent,
      ifMatchVersion: docsRef.current[threadId]?.version,
    });
    // `applied`, `merged` and `noop` all return authoritative state; reconcile
    // to it so a racing edit by another participant is never rendered stale.
    if (!result.doc) return;
    const doc = result.doc;
    setDocsByThread((prev) => ({ ...prev, [threadId]: doc }));
    if (result.participantIds || typeof result.canWrite === 'boolean') {
      setMetaByThread((prev) => ({
        ...prev,
        [threadId]: {
          canWrite: result.canWrite ?? prev[threadId]?.canWrite ?? false,
          participantIds: result.participantIds ?? prev[threadId]?.participantIds ?? [],
        },
      }));
    }
  }, [enabled, activeThreadId, metaByThread]);

  const onCycleStatus = useCallback(() => {
    if (!activeTags) return;
    void applyPatch({ set: { status: nextInRing(TAG_STATUSES, activeTags.status) } });
  }, [activeTags, applyPatch]);

  const onCyclePriority = useCallback(() => {
    if (!activeTags) return;
    void applyPatch({ set: { priority: nextInRing(PRIORITY_LADDER, activeTags.priority) } });
  }, [activeTags, applyPatch]);

  /**
   * Cycle owner over `[unassigned, ...participants]`. The candidate list comes
   * from the server's registry-authoritative participant ids, so the UI can
   * only ever propose an owner the server would accept.
   */
  const onCycleOwner = useCallback(() => {
    const threadId = (activeThreadId ?? '').trim();
    if (!activeTags || !threadId) return;
    const participants = metaByThread[threadId]?.participantIds ?? [];
    if (!participants.length) return;
    const ring: Array<string | null> = [null, ...participants];
    const index = ring.indexOf(activeTags.ownerUserId ?? null);
    const next = ring[((index < 0 ? 0 : index) + 1) % ring.length];
    void applyPatch({ set: { ownerUserId: next } });
  }, [activeTags, activeThreadId, metaByThread, applyPatch]);

  const onRemoveLabel = useCallback((label: string) => {
    const token = String(label || '').trim().toLowerCase();
    if (!token) return;
    void applyPatch({ removeLabels: [token] });
  }, [applyPatch]);

  const onAddLabel = useCallback(() => {
    const current = new Set(activeTags?.workflowLabels ?? []);
    const next = DEFAULT_WORKFLOW_LABEL_ALLOWLIST.find((label) => !current.has(label));
    if (!next) return;
    void applyPatch({ addLabels: [next] });
  }, [activeTags, applyPatch]);

  return {
    enabled,
    unavailable,
    filter,
    setFilter,
    activeTags,
    canEditActive,
    visibleThreads,
    tagsForThread,
    onCycleStatus,
    onCyclePriority,
    onCycleOwner,
    onRemoveLabel,
    onAddLabel,
  };
}
